import { CustomPackage } from '@/types/packages';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, Star, VerifiedIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MarketplaceCard } from '@/components/ui/marketplace-card';

interface CustomPackageCardProps {
  package: CustomPackage;
  showActions?: boolean;
}

export default function CustomPackageCard({ package: pkg, showActions = true }: CustomPackageCardProps) {
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(pkg.price_cents / 100);

  // Sort features by sort_order
  const sortedFeatures = [...pkg.features].sort((a, b) => a.sort_order - b.sort_order);
  
  // Use slug if available, otherwise use ID
  const detailUrl = `/custom-packages/${pkg.slug || pkg.id}`;
  const purchaseUrl = `/custom-packages/${pkg.slug || pkg.id}/purchase`;

  // Get seller data
  const seller = pkg.seller_profile || {
    name: "Loading...",
    verified: false,
    slug: "",
    logo_url: "",
    rating: 0,
    review_count: 0
  };

  // Format seller rating
  const rating = seller.rating || 4.8; // Fallback rating if none provided
  const reviewCount = seller.review_count || 10;
  
  // Title component
  const titleComponent = (
    <Link to={detailUrl} className="hover:underline">
      <h3 className="text-sm font-semibold min-h-[2.5rem] line-clamp-2">
        {pkg.title}
      </h3>
    </Link>
  );
  
  // Badge for active status
  const statusBadge = (
    <Badge variant={pkg.is_active ? "default" : "secondary"}>
      {pkg.is_active ? "Active" : "Inactive"}
    </Badge>
  );
  
  // Price and turnaround info
  const priceInfo = (
    <div className="flex items-center justify-between mb-3 w-full overflow-hidden mt-2">
      <span className="text-base font-bold truncate mr-2">{formattedPrice}</span>
      <div className="flex items-center text-xs text-muted-foreground flex-shrink-0">
        <Clock className="w-3 h-3 mr-1" />
        {pkg.estimated_turnaround_days} days
      </div>
    </div>
  );
  
  // Features list
  const featuresSection = (
    <div className="space-y-2 w-full overflow-hidden">
      {sortedFeatures.slice(0, 3).map((feature) => (
        <div key={feature.id} className="flex items-start gap-2">
          <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium truncate">{feature.title}</p>
            {feature.description && (
              <p className="text-xs text-muted-foreground line-clamp-1">{feature.description}</p>
            )}
          </div>
        </div>
      ))}
      {sortedFeatures.length > 3 && (
        <p className="text-xs text-muted-foreground">
          +{sortedFeatures.length - 3} more features
        </p>
      )}
    </div>
  );
  
  // Seller info component - moved to bottom for consistency
  const sellerInfo = (
    <Link 
      to={`/sellers/${seller.slug || ""}`}
      className="flex items-center gap-1 hover:bg-gray-50 p-1 rounded-md mt-2 w-full overflow-hidden"
    >
      <Avatar className="h-5 w-5 flex-shrink-0">
        <AvatarImage src={seller.logo_url} alt={seller.name} />
        <AvatarFallback>{seller.name?.substring(0, 2).toUpperCase() || "SP"}</AvatarFallback>
      </Avatar>
      <span className="text-xs font-medium text-gray-700 flex items-center gap-1 min-w-0 truncate">
        {seller.name}
        {seller.verified && (
          <VerifiedIcon className="h-3 w-3 text-blue-500 flex-shrink-0" />
        )}
      </span>
      <div className="ml-auto flex items-center text-xs text-amber-500 flex-shrink-0">
        <Star className="fill-amber-500 h-3 w-3 mr-0.5" />
        <span>{rating.toFixed(1)}</span>
        <span className="text-gray-400 ml-1">({reviewCount})</span>
      </div>
    </Link>
  );
  
  // Custom footer for purchase button alongside view details
  const cardActions = showActions ? (
    <div className="flex justify-between w-full gap-2">
      <Button variant="outline" size="sm" asChild className="flex-1">
        <Link to={detailUrl}>View Details</Link>
      </Button>
      <Button size="sm" asChild className="flex-1">
        <Link to={purchaseUrl}>Purchase</Link>
      </Button>
    </div>
  ) : null;

  return (
    <MarketplaceCard
      title={titleComponent}
      titleExtra={statusBadge}
      description={pkg.description}
      className="h-full"
      footer={cardActions}
      bottomInfo={sellerInfo}
      linkTo={!showActions ? detailUrl : undefined}
      actionLabel="View Details"
    >
      {priceInfo}
      {featuresSection}
    </MarketplaceCard>
  );
} 